import { CornerIconButton } from "@/src/components/buttons";
import { openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import {
  Service,
  documentsThemes,
  footerTypes,
  headerTypes,
  themeColors,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { isAdmin, isCabinet, isSuperAdmin } from "@/src/utils/utils";
import { useState } from "react";
import { refreshUser } from "../users/usersHook";

export function EditServiceDocumentButton({
  onOpen,
  onClose,
  onChange,
}: {
  onOpen: () => void;
  onClose: () => void;
  onChange: () => void;
}) {
  let user = USER.useSubscribe(`editDocumentConfigButtonUser`);
  let service = user?.service;
  if (!service) throw new Error("Le service n'est pas défini");
  if (!isAdmin(USER.get()) && !isSuperAdmin(USER.get()) && !isCabinet())
    return <></>;

  return (
    <CornerIconButton
      type="edit"
      onClick={async () => {
        onOpen();

        await openDocumentServiceInfoDrawer({
          service: service!,
          onConfirm: (newValue) => {
            service = newValue;
            onChange();
            onClose();
          },
          onCancel: (newValue) => {
            service = newValue;
            onChange();
            onClose();
          },
          onChange: (newValue) => {
            service = newValue;
            onChange();
          },
        });
      }}
    ></CornerIconButton>
  );
}

export async function openDocumentServiceInfoDrawer({
  service,
  onChange,
  onCancel,
  onConfirm,
}: {
  service: Service;
  onCancel: (newValue: Service) => void;
  onChange: (newValue: Service) => void;
  onConfirm: (newValue: Service) => void;
}) {
  let oldService = JSON.parse(JSON.stringify(service));
  let isConfirmed = false;
  await openDrawer({
    id: "editDocumentConfig",
    title: "Modifier le thème",
    content: (
      <div className="c1 gap15">
        <DocumentServiceInfoForm
          current={service!}
          onChange={(newValue) => {
            service = newValue;
            USER.setIndex("service", service!);
            onChange(newValue);
          }}
        ></DocumentServiceInfoForm>
      </div>
    ),
    onConfirm: async () => {
      isConfirmed = true;
      await apiFetch("services", "addOne", { service: service! });

      await refreshUser();
      isConfirmed = true;

      onChange(service!);
    },
  });
  if (!isConfirmed) {
    service = oldService;
    USER.setIndex("service", service!);
    onCancel(service!);
  }
}

export function DocumentServiceInfoForm({
  current,
  onChange,
}: {
  current: Service;
  onChange: (newValue: Service) => void;
}) {
  let [service, setService] = useState(current);
  function setNewValue(newValue: Service) {
    setService(newValue);
    onChange(newValue);
  }

  return (
    <div className="grid c1 " style={{ gap: "50px" }}>
      <div className="c1 gap15">
        <Input
          name={"Thème"}
          type={"select"}
          options={Object.values(documentsThemes).map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          value={service.theme}
          onChange={(newValue) => {
            setNewValue({ ...service, theme: newValue });
          }}
        />

        <Input
          name={"Couleur"}
          type={"select"}
          options={Object.values(themeColors).map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          value={service.color}
          onChange={(newValue) => {
            setNewValue({ ...service, color: newValue });
          }}
        />
      </div>

      <div className="c1 gap15">
        <div className="mtop20 f18 bold">En tête</div>

        <Input
          name={"Type d'entête"}
          type={"select"}
          options={Object.values(headerTypes).map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          value={service.headerType}
          onChange={(newValue) => {
            setNewValue({ ...service, headerType: newValue });
          }}
        />

        <Input
          name={"Texte 1"}
          type={"text"}
          value={service.h1}
          onChange={(newValue) => {
            setNewValue({ ...service, h1: newValue });
          }}
        />
        <Input
          name={"Texte 2"}
          type={"text"}
          value={service.h2}
          onChange={(newValue) => {
            setNewValue({ ...service, h2: newValue });
          }}
        />
        <Input
          name={"Texte 3"}
          type={"text"}
          value={service.h3}
          onChange={(newValue) => {
            setNewValue({ ...service, h3: newValue });
          }}
        />
      </div>

      <div className="c1 gap15">
        <div className="mtop20 f18 bold">Logo (en tête)</div>

        <Input
          name={"Logo"}
          type={"upload"}
          value={service.logo}
          onChange={(newValue) => {
            setNewValue({ ...service, logo: newValue });
          }}
        />
        <div className="c4 gap10">
          <Input
            name={"Hauteur"}
            type={"number"}
            value={service.logoHeight}
            onChange={(newValue) => {
              setNewValue({ ...service, logoHeight: newValue });
            }}
          />
          <Input
            name={"Largeur"}
            type={"number"}
            value={service.logoWidth}
            onChange={(newValue) => {
              setNewValue({ ...service, logoWidth: newValue });
            }}
          />
          <Input
            name={"Opacité"}
            type={"number"}
            value={service.logoOpacity}
            onChange={(newValue) => {
              setNewValue({ ...service, logoOpacity: newValue });
            }}
          />
          <Input
            name={"Padding"}
            type={"number"}
            value={service.logoPadding}
            onChange={(newValue) => {
              setNewValue({ ...service, logoPadding: newValue });
            }}
          />
        </div>
        <Input
          name={"Afficher Code barre à la place du logo"}
          type={"chips"}
          value={service.showHeaderBarcodeInsteadOfLogo ? "true" : "false"}
          options={[
            {
              id: "true",
              name: "Oui",
            },
            {
              id: "false",
              name: "Non",
            },
          ]}
          onChange={(newValue) => {
            setNewValue({
              ...service,
              showHeaderBarcodeInsteadOfLogo: newValue === "true",
            });
          }}
        />
      </div>

      <div className="c1 gap15">
        <div className="mtop20 f18 bold">Pied de page</div>
        <Input
          name={"Type de pied de page"}
          type={"select"}
          options={Object.values(footerTypes).map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          value={service.footerType}
          onChange={(newValue) => {
            setNewValue({ ...service, footerType: newValue });
          }}
        />
        <Input
          name={"Texte 1 (pied de page)"}
          type={"text"}
          value={service.footerH1}
          onChange={(newValue) => {
            setNewValue({ ...service, footerH1: newValue });
          }}
        />
        <Input
          name={"Texte 2 (pied de page)"}
          type={"text"}
          value={service.footerH2}
          onChange={(newValue) => {
            setNewValue({ ...service, footerH2: newValue });
          }}
        />
        <Input
          name={"Texte 3 (pied de page)"}
          type={"text"}
          value={service.footerH3}
          onChange={(newValue) => {
            setNewValue({ ...service, footerH3: newValue });
          }}
        />
        <Input
          name={"Texte 4 (pied de page)"}
          type={"text"}
          value={service.footerH4}
          onChange={(newValue) => {
            setNewValue({ ...service, footerH3: newValue });
          }}
        />
        <Input
          name={"Texte 5 (pied de page)"}
          type={"text"}
          value={service.footerH5}
          onChange={(newValue) => {
            setNewValue({ ...service, footerH5: newValue });
          }}
        />
        <Input
          name={"Texte 6 (pied de page)"}
          type={"text"}
          value={service.footerH6}
          onChange={(newValue) => {
            setNewValue({ ...service, footerH6: newValue });
          }}
        />
        <Input
          name={"Adresse"}
          type={"text"}
          value={service.footerAddress}
          onChange={(newValue) => {
            setNewValue({ ...service, footerAddress: newValue });
          }}
        />
        <Input
          name={"N° de téléphone 1"}
          type={"text"}
          value={service.footerPhoneNumber1}
          onChange={(newValue) => {
            setNewValue({ ...service, footerPhoneNumber1: newValue });
          }}
        />
        <Input
          name={"N° de téléphone 2"}
          type={"text"}
          value={service.footerPhoneNumber2}
          onChange={(newValue) => {
            setNewValue({ ...service, footerPhoneNumber2: newValue });
          }}
        />
      </div>

      <div className="c1 gap15">
        <div className="mtop20 f18 bold">Logo (pied de page)</div>

        <Input
          name={"Logo"}
          type={"upload"}
          value={service.footerLogo}
          onChange={(newValue) => {
            setNewValue({ ...service, footerLogo: newValue });
          }}
        />
        <div className="c4 gap10">
          <Input
            name={"Hauteur"}
            type={"number"}
            value={service.footerLogoHeight}
            onChange={(newValue) => {
              setNewValue({ ...service, footerLogoHeight: newValue });
            }}
          />
          <Input
            name={"Largeur"}
            type={"number"}
            value={service.footerLogoWidth}
            onChange={(newValue) => {
              setNewValue({ ...service, footerLogoWidth: newValue });
            }}
          />
          <Input
            name={"Opacité"}
            type={"number"}
            value={service.footerLogoOpacity}
            onChange={(newValue) => {
              setNewValue({ ...service, footerLogoOpacity: newValue });
            }}
          />
          <Input
            name={"Padding"}
            type={"number"}
            value={service.footerLogoPadding}
            onChange={(newValue) => {
              setNewValue({ ...service, footerLogoPadding: newValue });
            }}
          />
        </div>
        <Input
          name={"Afficher Code barre à la place du logo"}
          type={"chips"}
          value={service.showFooterBarcodeInsteadOfLogo ? "true" : "false"}
          options={[
            {
              id: "true",
              name: "Oui",
            },
            {
              id: "false",
              name: "Non",
            },
          ]}
          onChange={(newValue) => {
            setNewValue({
              ...service,
              showFooterBarcodeInsteadOfLogo: newValue === "true",
            });
          }}
        />
      </div>

      <div className="c1 gap15">
        <div className="mtop20 f18 bold">Image de fond</div>

        <Input
          name={"Image"}
          type={"upload"}
          value={service.backgroundImage}
          onChange={(newValue) => {
            setNewValue({ ...service, backgroundImage: newValue });
          }}
        />

        <div className="c3 gap10">
          <Input
            name={"Hauteur"}
            type={"number"}
            value={service.backgroundImageHeight}
            onChange={(newValue) => {
              setNewValue({ ...service, backgroundImageHeight: newValue });
            }}
          />
          <Input
            name={"Largeur"}
            type={"number"}
            value={service.backgroundImageWidth}
            onChange={(newValue) => {
              setNewValue({ ...service, backgroundImageWidth: newValue });
            }}
          />
          <Input
            name={"Opacité"}
            type={"number"}
            value={service.backgroundImageOpacity}
            onChange={(newValue) => {
              setNewValue({ ...service, backgroundImageOpacity: newValue });
            }}
          />
        </div>
      </div>
      <div className="c1 gap15">
        <div className="mtop20 f18 bold">Informations générales</div>

        <Input
          name={"Nom du cabinet"}
          type={"text"}
          value={service.name}
          onChange={(newValue) => {
            setNewValue({ ...service, name: newValue });
          }}
        />
      </div>
    </div>
  );
}
